// Hostile-mob rules (MB.6). PURE (ENG-1): the spawn/conversion conditions that
// don't fit the melee/ranged archetypes — phantom insomnia spawning, the zombie-
// villager cure timer, and the infested-block ↔ silverfish mapping. The host
// drives entities/blocks over these; keeping the maths pure makes the "phantom
// only after 3 sleepless nights" and "cure 3–5 min" rules unit-testable.

// ── Phantoms (insomnia) ──────────────────────────────────────────────────────
/** Game ticks awake before phantoms begin to spawn (Java: 3 days = 72000). */
export const INSOMNIA_TICKS = 3 * 24000;

/**
 * Whether phantoms may spawn above a player this attempt: they only come at night
 * under open sky once the player has gone `INSOMNIA_TICKS` without sleeping/dying
 * (their rest timer). Matches Java's `timeSinceRest > 72000` gate.
 */
export function phantomsCanSpawn(timeSinceRest: number, isNight: boolean, skyVisible: boolean): boolean {
  return isNight && skyVisible && timeSinceRest > INSOMNIA_TICKS;
}

/** Phantom group size per successful insomnia attempt (Java 1 + rand(1+difficulty*...)). */
export function phantomPackSize(roll: number): number {
  return 1 + Math.floor(roll * 4); // 1..4
}

// ── Zombie-villager cure ─────────────────────────────────────────────────────
/** Cure conversion timer window (Java 3–5 minutes). */
export const CURE_MIN_TICKS = 3 * 60 * 20; // 3600
export const CURE_MAX_TICKS = 5 * 60 * 20; // 6000

/** Curing needs the zombie villager to be under Weakness and then be fed a
 *  golden apple (either the plain or enchanted one). */
export function canStartCure(hasWeakness: boolean, fedItem: string): boolean {
  return hasWeakness && (fedItem === 'golden_apple' || fedItem === 'enchanted_golden_apple');
}

/** A seeded conversion duration in the 3–5 minute window. */
export function cureDuration(roll: number): number {
  const span = CURE_MAX_TICKS - CURE_MIN_TICKS;
  return CURE_MIN_TICKS + Math.floor(Math.max(0, Math.min(1, roll)) * span);
}

// ── Zombie → drowned conversion (E1, M18) ────────────────────────────────────
/** Ticks a zombie must stay submerged before it converts to a drowned (Java 30 s =
 *  600 gt; kept shorter here so it is observable + testable). */
export const DROWN_TICKS = 15 * 20; // 300

/** True once a zombie has been underwater long enough to become a drowned. */
export function shouldDrown(submergedTicks: number): boolean {
  return submergedTicks >= DROWN_TICKS;
}

// ── Silverfish / infested blocks ─────────────────────────────────────────────
/** Infested block → the block it masquerades as (breaking one releases a silverfish). */
export const INFESTED: Record<string, string> = {
  infested_stone: 'stone',
  infested_cobblestone: 'cobblestone',
  infested_stone_bricks: 'stone_bricks',
  infested_deepslate: 'deepslate',
};

export function isInfested(block: string): boolean {
  return block in INFESTED;
}

/** The non-infested block an infested one drops when silk-touched, or null. */
export function hostBlockOf(block: string): string | null {
  return INFESTED[block] ?? null;
}

/** Breaking an infested block without silk touch spawns a silverfish (releases it). */
export function releasesSilverfish(block: string, silkTouch: boolean): boolean {
  return isInfested(block) && !silkTouch;
}
